import { useState, useEffect, useCallback, useMemo } from 'react';
import { loadState, saveState, saveOverrides } from './utils/storage';
import { generateSchedule, applyOverrides } from './utils/scheduler';
import MemberManager from './components/MemberManager';
import ScheduleView from './components/ScheduleView';
import StatsView from './components/StatsView';

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

export default function App() {
  const [page, setPage] = useState('schedule');
  const [state, setState] = useState(() => loadState());
  const [schedule, setSchedule] = useState([]);
  const [dirty, setDirty] = useState(false);

  const regenerate = useCallback((s) => {
    setSchedule(generateSchedule(s.facilitators, s.prayers, s.ushers, s.persons, s.year));
  }, []);

  useEffect(() => { regenerate(state); }, []); // eslint-disable-line

  // persons가 override 해석의 기준 (personId ↔ name)
  const effectiveSchedule = useMemo(
    () => applyOverrides(schedule, state.overrides, state.persons),
    [schedule, state.overrides, state.persons]
  );

  function handleMemberChange({ persons, facilitators, prayers, ushers }) {
    setState(prev => ({ ...prev, persons, facilitators, prayers, ushers }));
    setDirty(true);
  }

  function handleYearChange(e) {
    const year = parseInt(e.target.value);
    const next = { ...state, year };
    setState(next);
    saveState(next);
    regenerate(next);
    setDirty(false);
  }

  function handleSave() {
    saveState(state);
    regenerate(state);
    setDirty(false);
  }

  function handleOverride(dateKey, overrideData) {
    const next = { ...state.overrides };
    if (overrideData === null) delete next[dateKey];
    else next[dateKey] = overrideData;
    setState(prev => ({ ...prev, overrides: next }));
    saveOverrides(next);
  }

  return (
    <div className="app">
      <nav className="navbar">
        <span className="navbar-brand">⛪ 예배 사회 & 로비 안내 배정 시스템</span>
        <div className="navbar-nav">
          <button className={`nav-btn ${page === 'schedule' ? 'active' : ''}`} onClick={() => setPage('schedule')}>📅 스케줄</button>
          <button className={`nav-btn ${page === 'members'  ? 'active' : ''}`} onClick={() => setPage('members')}>👥 명단 관리</button>
          <button className={`nav-btn ${page === 'stats'    ? 'active' : ''}`} onClick={() => setPage('stats')}>📊 통계</button>
        </div>
        <div className="navbar-right">
          {dirty && (
            <button className="btn btn-success btn-sm" onClick={handleSave}>
              💾 저장 &amp; 재배정
            </button>
          )}
          <select className="year-selector" value={state.year} onChange={handleYearChange}>
            {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}년</option>)}
          </select>
        </div>
      </nav>

      <main className="main">
        {page === 'schedule' && (
          <ScheduleView
            schedule={effectiveSchedule}
            overrides={state.overrides}
            year={state.year}
            facilitators={state.facilitators}
            prayers={state.prayers}
            ushers={state.ushers}
            persons={state.persons}
            onOverride={handleOverride}
          />
        )}
        {page === 'stats' && (
          <StatsView
            schedule={effectiveSchedule}
            year={state.year}
            persons={state.persons}
            facilitators={state.facilitators}
            prayers={state.prayers}
            ushers={state.ushers}
          />
        )}
        {page === 'members' && (
          <MemberManager
            persons={state.persons}
            facilitators={state.facilitators}
            prayers={state.prayers}
            ushers={state.ushers}
            onChange={handleMemberChange}
          />
        )}
      </main>
    </div>
  );
}
